import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterOutlet } from '@angular/router';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { AuthService } from '@presencelearning/auth';
import {
  CommandPaletteComponent,
  CommandPaletteService,
} from '@presencelearning/ui/command-palette';
import { DrawerComponent } from '@presencelearning/ui/drawer';
import {
  NavigationComponent,
  NavigationTriggerDirective,
  PlUserSummaryComponent,
} from '@presencelearning/ui/navigation';
import { ThemeToggleComponent } from '@presencelearning/ui/theme';
import { TopbarComponent } from '@presencelearning/ui/topbar';
import { RoomLink } from '../lessonspace/room-link';
import { LogoComponent } from '../logo/logo.component';

@Component({
  imports: [
    RouterOutlet,
    TopbarComponent,
    NavigationComponent,
    NavigationTriggerDirective,
    PlUserSummaryComponent,
    DrawerComponent,
    ThemeToggleComponent,
    CommandPaletteComponent,
    MatIconModule,
    MatMenuModule,
    LogoComponent,
    TranslocoPipe,
  ],
  selector: 'app-shell',
  templateUrl: './shell.component.html',
})
export class ShellComponent {
  protected readonly commandPalette = inject(CommandPaletteService);
  protected readonly auth = inject(AuthService);
  private readonly roomLink = inject(RoomLink);
  private readonly transloco = inject(TranslocoService);

  protected readonly copyLabel = signal<'topbar.copyRoom' | 'topbar.copied'>('topbar.copyRoom');
  protected readonly roomId = this.roomLink.roomId;

  protected readonly activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  // No user session yet to persist a real language preference against, so a
  // manual topbar toggle is the only way to reach the non-default language
  // today - detectBrowserLocale only covers first-load, not switching.
  protected readonly nextLang = computed(() => (this.activeLang() === 'en' ? 'es' : 'en'));

  // pl-navigation takes `pages` as a plain array of resolved strings, not a
  // template expression the `transloco` pipe can re-render on its own.
  // selectTranslate() (not the eager translate()) is required here:
  // translate() reads Transloco's cache synchronously and returns the raw
  // key if the translation set hasn't finished loading yet, which it hasn't
  // on this computed's first (and, absent another language switch, only)
  // evaluation - selectTranslate() emits once loading completes and again
  // on every subsequent language change.
  private readonly navLabels = toSignal(
    this.transloco.selectTranslate<string[]>(['nav.home', 'nav.session', 'nav.about']),
    { initialValue: ['nav.home', 'nav.session', 'nav.about'] }
  );

  protected readonly navLinks = computed(() => {
    const [home, session, about] = this.navLabels();
    return [
      { label: home, routerLink: '/', icon: 'home' },
      { label: session, routerLink: '/session', icon: 'videocam' },
      { label: about, routerLink: '/about', icon: 'info' },
    ];
  });

  readonly drawerOpen = signal(false);

  toggleLanguage(): void {
    this.transloco.setActiveLang(this.nextLang());
  }

  openSearch(): void {
    this.drawerOpen.set(false);
    this.commandPalette.open();
  }

  openAiChat(): void {
    this.commandPalette.close();
    this.drawerOpen.set(true);
  }

  async copyRoom(): Promise<void> {
    const roomId = this.roomId();
    if (!roomId) {
      return;
    }
    const url = new URL(encodeURIComponent(roomId), `${window.location.origin}/`).href;
    try {
      await navigator.clipboard.writeText(url);
      this.copyLabel.set('topbar.copied');
    } catch {
      this.copyLabel.set('topbar.copyRoom');
    }
  }
}
